import { useCallback, useEffect, useState } from "react";
import { generatePath, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Utils
import { pathname } from "#src/config/pathname";
import { useAppSelector } from "#src/store/hooks";

// Assets
import "#src/components/endpoint/directory/departments/styles.scss";

// API related
import { IDirectoryDepartment } from "#src/services/endpoint/directory/types";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import directoryService from "#src/services/endpoint/directory";

// Components
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { LoaderV2 } from "#src/commonV2/LoadingState";

// Children
import { DepartmentDiagram } from "#src/components/endpoint/directory/departments/DepartmentDiagram";
import { EmptyState } from "#src/components/endpoint/directory/departments/EmptyState";

export const DirectoryDepartments: React.FC = () => {
  const { workspaceId } = useParams<"workspaceId">();
  const { t } = useTranslation("endpoint", { keyPrefix: "directory" });
  const { workspaces } = useAppSelector((state) => state.workspace);

  if (!workspaceId) {
    return null;
  }

  // --------------- DATA ---------------

  const [isLoading, setIsLoading] = useState(true);
  const [departments, setDepartments] = useState<IDirectoryDepartment[]>([]);

  // --------------- COMPUTED ---------------

  const workspaceName =
    workspaces.find((w) => w.id === workspaceId)?.name || workspaceId;

  // --------------- METHODS ---------------

  const getAllDepartments = useCallback(async () => {
    if (!workspaceId) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await directoryService.listAllDepartments(workspaceId);
      setDepartments(res);
    } catch (error) {
      apiErrorHandler(error);
    }

    setIsLoading(false);
  }, [workspaceId]);

  // ---------------EFFECTS ---------------

  useEffect(() => {
    getAllDepartments();
  }, [getAllDepartments]);

  // --------------- RENDER ---------------

  return (
    <div className="pt-5 pb-6 flex flex-col gap-5 min-h-screen">
      {/* Header (Breadcrumb + title) */}
      <div className="px-8 flex flex-col gap-4">
        <BreadcrumbV2
          levels={[
            {
              name: t("breadcrumb.levels.endpoint"),
              path: generatePath(pathname.ENDPOINT_OVERVIEW, { workspaceId }),
            },
          ]}
          currentPage={{
            name: t("breadcrumb.current.directory"),
            tab: t("breadcrumb.current.departments"),
          }}
        />

        <h1 className="text-display-xs font-semibold text-gray-v2-900">
          {t("breadcrumb.current.departments")}
        </h1>
      </div>
      {/* Header (Breadcrumb + title) end */}

      {/* Loading */}
      {isLoading && (
        <div className="justify-center flex items-center flex-1">
          <LoaderV2 />
        </div>
      )}
      {/* Loading end */}

      {/* Tree */}
      {!isLoading && departments.length > 0 && (
        <div className="px-8 flex flex-col gap-4">
          <DepartmentDiagram
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            departments={departments}
            setDepartments={setDepartments}
          />
        </div>
      )}
      {/* Tree end */}

      {/* Empty */}
      {!isLoading && !departments.length && (
        <EmptyState workspaceId={workspaceId} setDepartments={setDepartments} />
      )}
      {/* Empty end */}
    </div>
  );
};
